import type { OrchestratedOfferingListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import { useIsPast, parseOptionalISODate } from '@b-stock/fe-core/bstock-react'

import useAccountId from '@/buyer-portal/helpers/useAccountId'
import { useIsServiceAccountOnly } from '@/buyer-portal/helpers/useIsServiceAccountOnly'

import { useIsSellerUserOfferingOwner } from '../../../useIsSellerUserOfferingOwner'
import { hasUserWonAnOffering } from '../../offering/hasUserWonAnOffering'

/**
 * Where a simple offering sits in the offer lifecycle, from this buyer's point
 * of view:
 *
 * - `live` — offer window open, nothing submitted (the interactive card)
 * - `submitted` — offer in, window still open, so it can still be updated
 * - `pending` — window ended, seller has not decided yet
 * - `accepted` / `declined` — seller decided, or the decision window lapsed
 *   without one, which counts as a decline
 * - `expired` — over with no offer of ours to report on
 * - `canceled` — the listing was canceled
 */
export type OfferDecisionState =
  | 'accepted'
  | 'canceled'
  | 'declined'
  | 'expired'
  | 'live'
  | 'pending'
  | 'submitted'

/**
 * Remember, the seller may accept early (before offerEndTime).
 */
export const getOfferDecisionState = ({
  buyerWon,
  decisionTimeEnded,
  hasOffer,
  isCanceled,
  offerTimeEnded,
  someoneElseWon,
}: {
  buyerWon: boolean
  decisionTimeEnded: boolean
  hasOffer: boolean
  isCanceled: boolean
  offerTimeEnded: boolean
  someoneElseWon: boolean
}): OfferDecisionState => {
  if (isCanceled) return 'canceled'

  // Without an offer of our own there is nothing to win or lose.
  if (!hasOffer) return offerTimeEnded || someoneElseWon ? 'expired' : 'live'

  if (buyerWon) return 'accepted'
  if (someoneElseWon) return 'declined'

  // We made an offer and nobody has won
  if (!offerTimeEnded) return 'submitted'
  return decisionTimeEnded ? 'declined' : 'pending'
}

export const useOfferDecisionState = (
  listing: OrchestratedOfferingListingDetails
): OfferDecisionState => {
  const { offering, offers, status } = listing
  const { decisionEndTime, offerEndTime } = offering ?? {}

  const accountId = useAccountId()
  const isSellerUserOfferingOwner = useIsSellerUserOfferingOwner({ offering })
  const isServiceAccountOnly = useIsServiceAccountOnly()
  const offerEndTimeIsPast = useIsPast(parseOptionalISODate(offerEndTime))
  const decisionEndTimeIsPast = useIsPast(parseOptionalISODate(decisionEndTime))

  const isCompleted = status === 'COMPLETED'
  const ignoreOffers = isSellerUserOfferingOwner || isServiceAccountOnly
  const winningAccountIds = offering?.winningAccountIds

  return getOfferDecisionState({
    buyerWon: hasUserWonAnOffering({
      ignoreOffers,
      loggedInAccountId: accountId ?? '',
      offering: offering ?? null,
      offers: offers ?? null,
    }),
    decisionTimeEnded: decisionEndTimeIsPast || isCompleted,
    hasOffer: !!offers?.length && !ignoreOffers,
    isCanceled: status === 'CANCELED',
    offerTimeEnded: offerEndTimeIsPast || isCompleted,
    someoneElseWon:
      !!winningAccountIds?.length &&
      !winningAccountIds.includes(accountId ?? ''),
  })
}
